// ============================================================
// MOVEMENT - how far a combatant can actually get.
//
// Not "a circle of radius N": a circle is a lie the moment there is a
// wall in it. This is a shortest-path flood fill over the tile grid, so
// the highlighted area is the set of tiles genuinely REACHABLE within
// the budget, around obstacles rather than through them.
//
// The budget comes from the character sheet's own `movement` block
// (half / full / charge / run, in metres) - the app has carried those
// numbers since `character/factories.ts`, so nothing here invents a rule.
//
// Pure. Zero React dependency.
// ============================================================
import type { Movement } from '../character/types';
import type { Solid, TerrainKind, Vec3 } from './types';
import type { Boundary, Vec2 } from './boundary';
import { boundaryContains, boundaryCentre, boundaryExtent } from './boundary';
import { solidGeometry } from './solids';
import { solidIndex } from './solidIndex';
import { containsPoint } from './lineOfSight';
import type { Surface } from './reconcile';
import { surfaceCandidatesAt, reachableSurface, arrivalSurface } from './reconcile';
import { climbCharge } from './climb';
import { MinHeap } from './minHeap';
import type { SightOptions } from './lineOfSight';

/** Difficult ground costs double. Dangerous ground costs normal - it hurts, it does not slow. */
export const DIFFICULT_MULTIPLIER = 2;

const COST_MULTIPLIER: Record<TerrainKind, number> = {
  normal: 1,
  difficult: DIFFICULT_MULTIPLIER,
  dangerous: 1,
  impassable: Infinity,
};

/** Worst-first, so a patch of mud inside a wall reads as wall. */
const SEVERITY: TerrainKind[] = ['normal', 'dangerous', 'difficult', 'impassable'];

export type MovementMode = keyof Movement;
/** Every mode, in the order the bands grow: the one runtime list the type's readers check against. */
export const MOVEMENT_MODES: readonly MovementMode[] = ['half', 'full', 'charge', 'run'];

/** The distance this character covers with that action, in metres. */
export function movementBudget(movement: Movement | undefined, mode: MovementMode): number {
  return movement?.[mode] ?? 0;
}

/**
 * The terrain where somebody actually IS: the worst of what they are
 * standing on or in.
 *
 * THE PROBE GOES JUST BELOW THE FEET, not just above the ground. Those
 * were the same point for as long as everyone stood on the floor, and
 * they stopped being the same the moment a marker could be put on a
 * gantry. A fixed ankle-height probe reads the inside of whatever you
 * are standing on top of, so the roof of a five-metre block reported the
 * block - "Impassable" - and froze whoever was up there (v7.97.0).
 *
 * Just BELOW, because a surface is the top of the thing that makes it: a
 * painted zone is a 5cm solid and standing on it means being a hair
 * inside it. A hair above would float over every zone on the map.
 *
 * With no height given it answers about the ground, which is what every
 * caller written before this expects.
 */
export function terrainAt(x: number, z: number, solids: readonly Solid[], options: MovementOptions = {}): TerrainKind {
  const includeHidden = options.includeHidden !== false;
  const probe: Vec3 = { x, y: options.atHeight === undefined ? 0.01 : options.atHeight - 0.01, z };
  let worst: TerrainKind = 'normal';
  for (const solid of solids) {
    if (couldWorsen(solid, worst, includeHidden) && containsPoint(solidGeometry(solid), probe)) worst = solid.terrain;
  }
  return worst;
}

/** Is this solid worth the containment test - visible, and worse than what we already have? */
function couldWorsen(solid: Solid, worst: TerrainKind, includeHidden: boolean): boolean {
  if (solid.hidden && !includeHidden) return false;
  if (solid.terrain === 'normal') return false;
  return SEVERITY.indexOf(solid.terrain) > SEVERITY.indexOf(worst);
}

export interface ReachableTile {
  x: number;
  z: number;
  /** Metres spent getting here by the cheapest route. */
  cost: number;
  terrain: TerrainKind;
  /**
   * The height stood on at this tile, in metres.
   *
   * The fill always worked this out and threw it away, so everything
   * downstream assumed y=0 - which is why a character on a roof got
   * their movement bands painted on the floor below them (v7.97.0).
   *
   * OPTIONAL because a tile can be hand-built: `reachableFrom` always
   * fills it in, and every consumer treats its absence as the ground.
   */
  y?: number;
  /**
   * The tile this one was cheapest to reach FROM, as a grid key.
   *
   * The flood fill has always known this and threw it away. One field per
   * tile turns "you can get there" into "here is how you get there",
   * which is the whole difference between a highlighted area and a
   * movement vector. Absent on the tile you started from.
   */
  from?: string;
}

/** The grid key a tile is stored under. Rounding, not truncation. */
export function tileKey(x: number, z: number, tileSize: number): string {
  const tile = tileSize > 0 ? tileSize : 1;
  return `${Math.round(x / tile)},${Math.round(z / tile)}`;
}

/** A reach indexed by tile key: built once per reach, so a point's tile is one lookup, not a scan of every tile. */
export function indexReach<T extends { x: number; z: number }>(tiles: readonly T[], tileSize: number): Map<string, T> {
  return new Map(tiles.map((t) => [tileKey(t.x, t.z, tileSize), t]));
}

/** The reach tile a ground point falls in, or null (no point, or off the reach). */
export function tileAt<T>(index: ReadonlyMap<string, T>, point: { x: number; z: number } | null, tileSize: number) {
  return point ? (index.get(tileKey(point.x, point.z, tileSize)) ?? null) : null;
}

/**
 * Is this tile centre inside the arena?
 *
 * Nudged a micron towards the middle first: on a 20 m arena with 1 m
 * tiles the outermost centres land exactly on the wall, where ray
 * casting is a coin toss. A tile centred on the arena's own edge is
 * inside it.
 */
function insideArena(boundary: Boundary, x: number, z: number): boolean {
  if (boundaryContains(boundary, x, z)) return true;
  const c = boundaryCentre(boundary);
  const len = Math.hypot(c.x - x, c.z - z);
  if (len < 1e-9) return true;
  return boundaryContains(boundary, x + ((c.x - x) / len) * 1e-6, z + ((c.z - z) / len) * 1e-6);
}

const DIAGONAL = Math.SQRT2;

/** What movement asks of the map, on top of what sight asks. */
export interface MovementOptions extends SightOptions {
  /**
   * How far UP a single step may go, in metres. Absent means height is
   * not considered at all, which is what every caller written before
   * v7.27.0 expects.
   *
   * This one number is what separates climbing a kerb from scaling a
   * wall, and it gives stairs, ramps and ledges for nothing: a route up
   * three one-metre steps is three legal steps, and the same three
   * metres in one go is not a step at all.
   */
  maxStep?: number;
  /**
   * Metres of movement charged per metre of RISE. Zero means climbing is
   * free, which is what it was before v7.95.0.
   */
  climbCost?: number;
  /** Where the ground itself is, so a trench is not a cliff. */
  groundAt?: (x: number, z: number) => number;
  /**
   * The COST of ground stops mattering; walls still do.
   *
   * A Hoverer skims the mud and still meets the barricade, which is a
   * different exemption from a Flyer's and needs its own word.
   */
  ignoresGoing?: boolean;
  /**
   * The height the going is being asked about - the mover's feet.
   *
   * Absent means the ground, which is what every caller written before
   * v7.97.0 means. See `terrainAt`.
   */
  atHeight?: number;
  /**
   * What storey the fill STARTS on.
   *
   * A marker on a gantry floods the gantry, not the floor beneath it.
   * Absent means the surface at the starting column, which is the answer
   * for anybody standing on the ground.
   */
  startY?: number;
}

/**
 * Every tile reachable from `from` within `budget` metres.
 *
 * Eight-way movement with diagonals at √2, which tracks true distance far
 * better than a four-way grid - on open ground the result is a disc, as a
 * player expects, rather than a diamond.
 */
export function reachableFrom(
  from: Vec2,
  budget: number,
  boundary: Boundary,
  solids: Solid[],
  options: MovementOptions = {},
): ReachableTile[] {
  const fill = startFill({ from, budget, boundary, solids, options });
  while (fill.heap.size) {
    const top = fill.heap.pop()!;
    const here = fill.best.get(top.key)!;
    // A stale heap entry: we already found a cheaper way in.
    if (top.cost > here.cost + 1e-9) continue;
    for (const [dx, dz] of NEIGHBOURS) relax(fill, top.key, here, dx, dz);
  }
  return [...fill.best.values()];
}

/**
 * The eight steps out of a tile, in the order the fill has always tried
 * them. THE ORDER MATTERS: on a tie the first route found is kept, so
 * reordering these would change which way `pathTo` walks.
 */
const NEIGHBOURS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, -1],
  [0, 1],
  [1, -1],
  [1, 0],
  [1, 1],
];

/** The climbing rules one fill runs under. */
interface ClimbRules {
  /**
   * Heights are only consulted when a step limit was given, so the cost
   * of the surface lookup is not paid by callers who do not want it.
   */
  climbing: boolean;
  maxStep: number;
  /**
   * A hoverer skims what would slow it, and a rise is a cost like any
   * other; a flyer never reaches this code at all, because the fill it
   * runs has no solids in it and so has no rise to charge for.
   */
  climbCost: number;
}

function climbRules(options: MovementOptions): ClimbRules {
  return {
    climbing: Number.isFinite(options.maxStep) && (options.maxStep as number) > 0,
    maxStep: options.maxStep ?? Infinity,
    climbCost: options.ignoresGoing ? 0 : Math.max(0, options.climbCost ?? 0),
  };
}

/** Everything one call of `reachableFrom` carries between its steps. */
interface Fill {
  tile: number;
  budget: number;
  boundary: Boundary;
  extent: ReturnType<typeof boundaryExtent>;
  rules: ClimbRules;
  /** The floor a step onto this column lands on, stepping from `fromY`. */
  surface: (x: number, z: number, fromY: number) => number;
  /** The going at a column, at the height it is being crossed. */
  kindAt: (x: number, z: number, atHeight: number) => TerrainKind;
  best: Map<string, ReachableTile>;
  heights: Map<string, number>;
  heap: MinHeap;
}

interface FillRequest {
  from: Vec2;
  budget: number;
  boundary: Boundary;
  solids: Solid[];
  options: MovementOptions;
}

/**
 * Set up one fill: its rules, its per-column caches and its first tile.
 *
 * The floor at a column (`surface`) is DELIBERATELY WITHOUT A FOOTING, unlike settling. The fill decides
 * what is enterable from `terrainAt`, which reads a solid's own terrain
 * kind and knows nothing about heights - so if a surface out of reach
 * were treated as a ceiling here, a wall's column would report ground
 * level and the fill would happily route straight through it.
 *
 * The consequence is that a gantry overhead makes the ground beneath it
 * unreachable, which is wrong and is written down as such
 * (dev-docs/gotchas.md). Fixing it properly means passability coming
 * from the step rule rather than from a height constant in
 * `derivePassability`, which is a change to how every wall on every
 * saved map behaves and wants asking about first.
 */
function startFill({ from, budget, boundary, solids, options }: FillRequest): Fill {
  const tile = boundary.tileSize > 0 ? boundary.tileSize : 1;
  const rules = climbRules(options);
  const key = (x: number, z: number) => tileKey(x, z, tile);
  const tops = surfaceCandidatesCache(solids, options, key);
  const fill: Fill = {
    tile,
    budget,
    boundary,
    extent: boundaryExtent(boundary),
    rules,
    surface: (x, z, fromY) => (rules.climbing ? arrivalSurface(tops(x, z), fromY) : 0),
    kindAt: goingCache(solids, options, rules.climbing, key),
    best: new Map(),
    heights: new Map(),
    heap: new MinHeap(),
  };
  seedFill(fill, from, options.startY, tops);
  return fill;
}

/**
 * ONE ANSWER PER COLUMN, for the life of this call.
 *
 * A tile is examined once from each of its eight neighbours and again
 * when it is settled, so without this the surface under it is derived
 * up to sixteen times - and the answer cannot change while the fill
 * runs, because the map is immutable. Together with the geometry cache
 * this is what stopped "choose Run" locking the app up (v7.33.0).
 *
 * THE CANDIDATES are cached, not an answer: which surface is a floor
 * depends on where you are stepping from - a gantry four metres up is
 * a ceiling to somebody on the ground and a floor to somebody already
 * on it. The expensive half does not depend on that, so it is still
 * one pass per column.
 */
function surfaceCandidatesCache(
  solids: Solid[],
  options: MovementOptions,
  key: (x: number, z: number) => string,
): (x: number, z: number) => Surface[] {
  const surfaces = new Map<string, Surface[]>();
  const index = solidIndex(solids);
  return (x, z) => {
    const k = key(x, z);
    const seen = surfaces.get(k);
    if (seen) return seen;
    // Only the solids filed under this point (solidIndex): the rest cannot stand here.
    const found = surfaceCandidatesAt(x, z, index.at(x, z), options.groundAt ? options.groundAt(x, z) : 0);
    surfaces.set(k, found);
    return found;
  };
}

/**
 * The going at a column, AT THE HEIGHT it is being crossed.
 *
 * Keyed by both, because they are genuinely different answers: the
 * inside of a block is not its roof. Two entries per column in the
 * worst case, against the flat sheet this used to be.
 */
function goingCache(
  solids: Solid[],
  options: MovementOptions,
  climbing: boolean,
  key: (x: number, z: number) => string,
): Fill['kindAt'] {
  const kinds = new Map<string, TerrainKind>();
  const index = solidIndex(solids);
  return (x, z, atHeight) => {
    const k = `${key(x, z)}@${atHeight.toFixed(3)}`;
    const seen = kinds.get(k);
    if (seen !== undefined) return seen;
    const found = terrainAt(x, z, index.at(x, z), climbing ? { ...options, atHeight } : options);
    const kind = options.ignoresGoing && found !== 'impassable' ? 'normal' : found;
    kinds.set(k, kind);
    return kind;
  };
}

/**
 * Put the starting tile in the fill.
 *
 * WHICH STOREY THE FILL STARTS ON. A marker on a gantry floods the
 * gantry; without `startY` the fill began at whatever the column's own
 * surface was and the bands appeared on the floor below them.
 *
 * THE TILE YOU ARE ON IS ALWAYS YOURS, however illegal it is. Someone
 * moved inside a wall while terrain was switched off has to be able to
 * walk out again once it is switched back on - they are warned, not
 * imprisoned. Nothing lets them back IN: the rule is about leaving.
 */
function seedFill(fill: Fill, from: Vec2, startY: number | undefined, tops: (x: number, z: number) => Surface[]): void {
  const snap = (n: number) => Math.round(n / fill.tile) * fill.tile;
  const start = { x: snap(from.x), z: snap(from.z) };
  const y = fill.rules.climbing ? (startY ?? reachableSurface(tops(start.x, start.z))) : 0;
  const k = tileKey(start.x, start.z, fill.tile);
  fill.best.set(k, { ...start, cost: 0, terrain: fill.kindAt(start.x, start.z, y), y });
  fill.heights.set(k, y);
  fill.heap.push(k, 0);
}

/** Is this neighbour worth considering at all - on the map and inside the arena? */
function onTheMap(fill: Fill, x: number, z: number): boolean {
  const { extent, tile } = fill;
  if (x < extent.min.x - tile || x > extent.max.x + tile) return false;
  if (z < extent.min.z - tile || z > extent.max.z + tile) return false;
  return insideArena(fill.boundary, x, z);
}

/**
 * Try the step from `here` one tile along (dx, dz), and record it if it
 * is the cheapest way in so far.
 *
 * WHERE THIS STEP PUTS YOU decides everything after it: walking under a
 * gantry stays underneath it; a wall in the way is climbed over. See
 * `arrivalSurface`.
 *
 * GOING UP COSTS SOMETHING. Every rise within a stride used to be free,
 * so a staircase cost exactly what the same distance of flat floor did.
 * `climbCost` is metres of movement per metre of rise; zero switches it
 * off, and a hoverer is given zero because `ignoresGoing` already means
 * costs stop mattering to it.
 */
function relax(fill: Fill, hereKey: string, here: ReachableTile, dx: number, dz: number): void {
  const x = here.x + dx * fill.tile;
  const z = here.z + dz * fill.tile;
  if (!onTheMap(fill, x, z)) return;
  const hereY = fill.heights.get(hereKey) ?? 0;
  const y = fill.surface(x, z, hereY);
  const terrain = fill.kindAt(x, z, y);
  const stuck = !Number.isFinite(COST_MULTIPLIER[here.terrain]);
  const multiplier = stepMultiplier(terrain, stuck);
  if (!Number.isFinite(multiplier)) return;
  const climbed = climbCharged(fill.rules, stuck, y - hereY);
  if (climbed === null) return;
  const step = fill.tile * (dx && dz ? DIAGONAL : 1) * multiplier + climbed * fill.rules.climbCost;
  offer(fill, { x, z, cost: here.cost + step, terrain, y, from: hereKey });
}

/**
 * What a step onto this terrain costs per metre.
 *
 * THE ESCAPE RULE. While you are still standing in something you could
 * never have entered, you may keep moving through it - a marker left
 * inside a wall while terrain was switched off has to be able to walk
 * out once it is switched back on. It stops the instant the route
 * reaches open ground, so nothing lets anyone back IN: this is about
 * leaving, and a wall three tiles thick is still three tiles to push
 * through.
 */
function stepMultiplier(terrain: TerrainKind, stuck: boolean): number {
  if (Number.isFinite(COST_MULTIPLIER[terrain])) return COST_MULTIPLIER[terrain];
  return stuck ? DIFFICULT_MULTIPLIER : Infinity;
}

/**
 * Metres of height charged for a step that rises (or drops) by `rise`,
 * or null when the step is refused.
 *
 * UP IS A CLIMB AND IT IS ALLOWED. A rise beyond one stride was refused
 * outright, which made every wall absolute and left `climbCost` billing
 * for nothing but kerbs - the whole climb mechanic was invisible
 * (v7.97.0). What stops somebody scaling a tower now is the BUDGET: five
 * metres at the trained rate is ten metres of movement, and thirty
 * metres is a day's work.
 *
 * AND DOWN IS A CLIMB TOO (v8.7.0). It used to be a drop you could
 * always take, free and at any depth - so the fastest way off a tower
 * was to walk off it, and the affordability readout had nothing to say
 * about a descent because the answer was always yes. `climbCharge` keeps
 * a stride free and charges the rest at the same rate as going up.
 *
 * UNLESS CLIMBING IS NOT BEING MODELLED AT ALL. `climbCost` is zero when
 * the house rule is off and for every caller written before v7.95.0, and
 * a climb that costs nothing would let anyone stroll up a tower for free
 * - strictly worse than the stride rule it replaced. With no price on
 * it, a rise beyond one stride is refused exactly as it always was.
 *
 * A DESCENT IS NEVER REFUSED, whatever the switch says. `rise` is
 * negative coming down, so the refusal never fires on a drop - and that
 * is deliberate rather than incidental: with climbing unpriced nobody
 * can get back UP either, so refusing the way down would turn every
 * roof into a prison.
 */
function climbCharged(rules: ClimbRules, stuck: boolean, rise: number): number | null {
  if (!rules.climbing || stuck) return 0;
  if (rise > rules.maxStep + 1e-9 && rules.climbCost <= 0) return null;
  return climbCharge(rise, rules.maxStep);
}

/** Record a candidate tile if it is affordable and the cheapest way in so far. */
function offer(fill: Fill, tile: ReachableTile): void {
  if (tile.cost > fill.budget + 1e-9) return;
  const k = tileKey(tile.x, tile.z, fill.tile);
  const known = fill.best.get(k);
  if (known && known.cost <= tile.cost + 1e-9) return;
  fill.best.set(k, tile);
  if (fill.rules.climbing) fill.heights.set(k, tile.y ?? 0);
  fill.heap.push(k, tile.cost);
}

/**
 * A flood's tiles by key, built once per flood (the perf overhaul): a drag asks `pathTo` on every pointer move of the
 * one flood it keeps, and rebuilding this map each time was the drag's largest cost after the flood itself. A flood's
 * tile list is never changed once made; one flood has one tile size.
 */
const tileIndexes = new WeakMap<readonly ReachableTile[], Map<string, ReachableTile>>();
function tileIndexOf(tiles: readonly ReachableTile[], tile: number): Map<string, ReachableTile> {
  let index = tileIndexes.get(tiles);
  if (!index) tileIndexes.set(tiles, (index = indexReach(tiles, tile)));
  return index;
}

/**
 * The cheapest route to a tile, start first, destination last.
 *
 * Reads the back-pointers the flood fill left behind, so it costs a walk
 * of the path's own length and cannot disagree with the highlighted area:
 * both come from the same search. Returns [] for somewhere the fill never
 * reached, which is the honest answer to "how do I get there?" when you
 * cannot.
 */
export function pathTo(tiles: ReachableTile[], to: Vec2, boundary: Boundary): ReachableTile[] {
  const tile = boundary.tileSize > 0 ? boundary.tileSize : 1;
  const byKey = tileIndexOf(tiles, tile);

  const path: ReachableTile[] = [];
  const seen = new Set<string>();
  let key: string | undefined = tileKey(to.x, to.z, tile);
  while (key && !seen.has(key)) {
    seen.add(key);
    const step: ReachableTile | undefined = byKey.get(key);
    if (!step) return [];
    path.push(step);
    key = step.from;
  }
  return path.reverse();
}

/** What the whole route costs, in metres. */
export function pathCost(path: ReachableTile[]): number {
  return path.length ? path[path.length - 1].cost : 0;
}

/** A stretch of one kind of ground the route crosses. */
export interface TerrainCrossed {
  terrain: TerrainKind;
  /** How far along the route it lasts, in metres. */
  metres: number;
}

/**
 * The notable ground a route crosses, worst first.
 *
 * Only what is worth a warning: open ground is the default and saying so
 * would bury the mud. The tile you start on is excluded - you are already
 * standing there, and telling someone their own square is dangerous the
 * moment they pick a destination is noise.
 */
export function pathTerrain(path: ReachableTile[]): TerrainCrossed[] {
  const metres = new Map<TerrainKind, number>();
  for (let i = 1; i < path.length; i++) {
    const step = path[i];
    if (step.terrain === 'normal') continue;
    const length = Math.hypot(step.x - path[i - 1].x, step.z - path[i - 1].z);
    metres.set(step.terrain, (metres.get(step.terrain) ?? 0) + length);
  }
  return [...metres.entries()]
    .map(([terrain, m]) => ({ terrain, metres: m }))
    .sort((a, b) => SEVERITY.indexOf(b.terrain) - SEVERITY.indexOf(a.terrain));
}

/** How much of a route was vertical: metres gained, metres lost. */
export interface Vertical {
  climbed: number;
  dropped: number;
}

/**
 * The rise and fall along a route, for saying so out loud.
 *
 * A SIBLING TO `pathTerrain`, not part of it: that one answers "how many
 * horizontal metres of notable ground does this cross", and folding rise
 * into it would turn `difficult 3.0 m` into a number that is neither a
 * length nor a cost.
 *
 * NO NEW FIELD IS NEEDED. Every tile already carries the height it is
 * walked at, and Dijkstra settles a node before relaxing from it - so
 * the difference between consecutive tiles IS the rise the fill priced.
 *
 * It reports GEOMETRY, not the bill. Someone escaping impassable ground
 * moves vertically for nothing (the fill exempts them so nobody is ever
 * imprisoned), and this still says how far they went, which is the
 * honest answer to "what did they just do".
 */
export function pathVertical(path: ReachableTile[]): Vertical {
  let climbed = 0;
  let dropped = 0;
  for (let i = 1; i < path.length; i++) {
    const rise = (path[i].y ?? 0) - (path[i - 1].y ?? 0);
    if (rise > 0) climbed += rise;
    else dropped -= rise;
  }
  return { climbed, dropped };
}
